/**
 * RL Loop Configuration Form
 *
 * Form for configuring and starting a new RL (Reinforcement Learning) Loop.
 * Allows setting up target personas, simulation config, stop conditions, and review settings.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  OutlinedInput,
  Slider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Switch
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as StartIcon,
  Settings as SettingsIcon,
  Science as SandboxIcon,
  Public as LiveIcon,
  Tune as TuneIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  getReviewerPersonas
} from '../../../services/advancedSimulationApi';
import { listSandboxPersonas } from '../../../services/researcherApi';
import { startRLLoop } from '../../../services/rlLoopApi';
import { getAvailableLLMModels, getLLMConfiguration } from '../../../services/api';

const FOCUS_AREAS = [
  { value: 'empathy', label: 'Empathy' },
  { value: 'technique', label: 'Technique' },
  { value: 'safety', label: 'Safety' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'pacing', label: 'Pacing' },
  { value: 'boundaries', label: 'Boundaries' },
  { value: 'cbc_effectiveness', label: 'CBC Effectiveness' },
  { value: 'parasocial_handling', label: 'Parasocial Handling' }
];

const LLM_PROVIDERS = [
  { value: 'vertex_ai', label: 'Vertex AI (Google)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Gemini' }
];

// Fallback models if API fails to load
const DEFAULT_LLM_MODELS = {
  vertex_ai: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-1.5-flash', 'gemini-1.5-pro'],
  openai: ['gpt-4o', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo'],
  anthropic: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro']
};

function RLLoopConfigForm({ token, onLoopStarted, isResearcher = false, initialConfig = null, initialName = '' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveClientPersonas, setLiveClientPersonas] = useState([]);
  const [liveTherapistPersonas, setLiveTherapistPersonas] = useState([]);
  const [sandboxClientPersonas, setSandboxClientPersonas] = useState([]);
  const [sandboxTherapistPersonas, setSandboxTherapistPersonas] = useState([]);
  const [reviewerPersonas, setReviewerPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(true);

  // Dynamic LLM models from platform configuration
  const [availableModels, setAvailableModels] = useState({});
  const [loadingModels, setLoadingModels] = useState(true);
  const [modelsInitialized, setModelsInitialized] = useState(false);

  // Persona filter for researchers
  const [personaFilter, setPersonaFilter] = useState('all'); // 'all', 'live', 'sandbox'

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Target personas (therapists to improve)
  const [targetPersonaKeys, setTargetPersonaKeys] = useState([]);
  const [targetPersonaSources, setTargetPersonaSources] = useState({}); // { key: 'live' | 'sandbox' }

  // Simulation config
  const [clientPersonaKeys, setClientPersonaKeys] = useState([]);
  const [clientPersonaSources, setClientPersonaSources] = useState({}); // { key: 'live' | 'sandbox' }
  const [therapistPersonaKeys, setTherapistPersonaKeys] = useState([]);
  const [maxTurns, setMaxTurns] = useState(50);
  const [runsPerIteration, setRunsPerIteration] = useState(3);

  // Loop config
  const [maxIterations, setMaxIterations] = useState(10);
  const [qualityThreshold, setQualityThreshold] = useState(80);

  // Stop conditions
  const [maxCostUSD, setMaxCostUSD] = useState(50);
  const [maxDurationMinutes, setMaxDurationMinutes] = useState(1440);
  const [maxConsecutiveNoImprovement, setMaxConsecutiveNoImprovement] = useState(3);

  // Improvement scope
  const [improveTherapistPrompt, setImproveTherapistPrompt] = useState(true);
  const [improveClientBehavior, setImproveClientBehavior] = useState(true);
  const [suggestCodeChanges, setSuggestCodeChanges] = useState(true);
  const [evaluateClientPersona, setEvaluateClientPersona] = useState(false);

  // Review config
  const [reviewProvider, setReviewProvider] = useState('vertex_ai');
  const [reviewModel, setReviewModel] = useState('gemini-2.5-flash');
  const [reviewTemperature, setReviewTemperature] = useState(0.3);
  const [focusAreas, setFocusAreas] = useState(['empathy', 'technique', 'safety']);
  const [maxSuggestionsPerIteration, setMaxSuggestionsPerIteration] = useState(5);

  // Parallel simulation concurrency
  const [maxConcurrentSimulations, setMaxConcurrentSimulations] = useState(5);

  // Simulation LLM config (model used to run therapy conversations)
  const [simulationProvider, setSimulationProvider] = useState('vertex_ai');
  const [simulationModel, setSimulationModel] = useState('gemini-2.5-flash');
  const [simulationTemperature, setSimulationTemperature] = useState(0.7);

  // Reviewer Persona config (for modality-specific evaluation)
  const [reviewerPersonaId, setReviewerPersonaId] = useState('');
  const [autoSelectReviewer, setAutoSelectReviewer] = useState(false);

  // Advanced Review Config (Gap 3)
  const [dimensionWeights, setDimensionWeights] = useState({
    empathy: 100,
    technique: 100,
    safety: 100,
    engagement: 100,
    pacing: 100,
    boundaries: 100
  });
  const [includeTranscriptExcerpts, setIncludeTranscriptExcerpts] = useState(true);
  const [customPromptSections, setCustomPromptSections] = useState({
    systemContext: '',
    evaluationCriteria: '',
    outputFormat: ''
  });

  // Auto-Rollback Configuration
  const [autoRollbackEnabled, setAutoRollbackEnabled] = useState(true);
  const [regressionThreshold, setRegressionThreshold] = useState(5);
  const [compareToIteration, setCompareToIteration] = useState('previous');

  // Particular focus text (free-text guidance for reviewer)
  const [particularFocusText, setParticularFocusText] = useState('');

  // Consolidation Configuration
  const [consolidationEnabled, setConsolidationEnabled] = useState(true);
  const [consolidationFrequency, setConsolidationFrequency] = useState(5);
  const [maxPromptLength, setMaxPromptLength] = useState(6000);

  // Load personas on mount
  useEffect(() => {
    const loadPersonas = async () => {
      try {
        // Load live personas and reviewer personas in parallel
        const [clientRes, therapistRes, reviewerRes] = await Promise.all([
          getAdvancedSimulationClientPersonas(token),
          getAdvancedSimulationTherapistPersonas(token),
          getReviewerPersonas(token)
        ]);
        setLiveClientPersonas(clientRes.personas || []);
        setLiveTherapistPersonas(therapistRes.personas || []);
        setReviewerPersonas(reviewerRes.personas || []);

        // Load sandbox personas if researcher
        if (isResearcher) {
          try {
            const sandboxRes = await listSandboxPersonas();
            const clientSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
              .filter(p => p.personaType === 'client');
            const therapistSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
              .filter(p => p.personaType === 'therapist');
            setSandboxClientPersonas(clientSandbox);
            setSandboxTherapistPersonas(therapistSandbox);
          } catch (sandboxErr) {
            console.error('Failed to load sandbox personas:', sandboxErr);
            // Don't fail completely if sandbox load fails
          }
        }
      } catch (err) {
        setError('Failed to load personas');
        console.error(err);
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadPersonas();
  }, [token, isResearcher]);

  // Load available LLM models from platform configuration
  useEffect(() => {
    const loadModels = async () => {
      try {
        setLoadingModels(true);
        const [modelsRes, configRes] = await Promise.all([
          getAvailableLLMModels(token, { activeOnly: true }),
          getLLMConfiguration(token)
        ]);

        // Group models by provider - store full model objects like SingleRunForm
        const grouped = { openai: [], gemini: [], vertex_ai: [], anthropic: [] };
        (modelsRes.models || []).forEach(model => {
          if (grouped[model.provider]) {
            grouped[model.provider].push(model);
          }
        });
        setAvailableModels(grouped);

        // Only set defaults on initial load, not when re-fetching
        if (!modelsInitialized && configRes.configuration) {
          const config = configRes.configuration;
          // Use platform defaults for reviewer LLM
          setReviewProvider(config.primaryProvider || 'vertex_ai');
          setReviewModel(config.primaryModel || 'gemini-2.5-flash');
          setModelsInitialized(true);
        }
      } catch (err) {
        console.error('Failed to load LLM models, using defaults:', err);
        // Keep using hardcoded defaults if API fails
      } finally {
        setLoadingModels(false);
      }
    };
    loadModels();
  }, [token, modelsInitialized]);

  // Pre-populate form from initialConfig (for clone workflow)
  useEffect(() => {
    if (!initialConfig) return;

    // Basic info
    if (initialName) setName(`${initialName} (copy)`);
    // Description from loop doc (not in config), set if available
    // Target personas
    const targetKeys = (initialConfig.targetPersonas || [])
      .filter(p => p.personaType === 'therapist')
      .map(p => p.personaKey);
    if (targetKeys.length > 0) setTargetPersonaKeys(targetKeys);

    // Simulation config
    const simConfig = initialConfig.simulationConfig || {};
    if (simConfig.clientPersonaKeys?.length > 0) setClientPersonaKeys(simConfig.clientPersonaKeys);
    if (simConfig.maxTurns) setMaxTurns(simConfig.maxTurns);
    if (simConfig.runsPerIteration) setRunsPerIteration(simConfig.runsPerIteration);
    if (simConfig.maxConcurrentSimulations) setMaxConcurrentSimulations(simConfig.maxConcurrentSimulations);
    if (simConfig.llmConfig) {
      if (simConfig.llmConfig.provider) setSimulationProvider(simConfig.llmConfig.provider);
      if (simConfig.llmConfig.model) setSimulationModel(simConfig.llmConfig.model);
      if (simConfig.llmConfig.temperature !== undefined) setSimulationTemperature(simConfig.llmConfig.temperature);
    }

    // Stop conditions & loop config
    if (initialConfig.maxIterations) setMaxIterations(initialConfig.maxIterations);
    if (initialConfig.qualityThreshold) setQualityThreshold(initialConfig.qualityThreshold);
    const stopCond = initialConfig.stopConditions || {};
    if (stopCond.maxCostUSD) setMaxCostUSD(stopCond.maxCostUSD);
    if (stopCond.maxDurationMinutes) setMaxDurationMinutes(stopCond.maxDurationMinutes);
    if (stopCond.maxConsecutiveNoImprovement) setMaxConsecutiveNoImprovement(stopCond.maxConsecutiveNoImprovement);

    // Improvement scope
    const scope = initialConfig.improvementScope || {};
    if (scope.therapistPrompt !== undefined) setImproveTherapistPrompt(scope.therapistPrompt);
    if (scope.clientPersonaBehavior !== undefined) setImproveClientBehavior(scope.clientPersonaBehavior);
    if (scope.suggestCodeChanges !== undefined) setSuggestCodeChanges(scope.suggestCodeChanges);
    if (scope.clientPersona !== undefined) setEvaluateClientPersona(scope.clientPersona);

    // Review config
    const reviewConf = initialConfig.reviewConfig || {};
    if (reviewConf.llmConfig) {
      if (reviewConf.llmConfig.provider) setReviewProvider(reviewConf.llmConfig.provider);
      if (reviewConf.llmConfig.model) setReviewModel(reviewConf.llmConfig.model);
      if (reviewConf.llmConfig.temperature !== undefined) setReviewTemperature(reviewConf.llmConfig.temperature);
    }
    if (reviewConf.focusAreas?.length > 0) setFocusAreas(reviewConf.focusAreas);
    if (reviewConf.maxSuggestionsPerIteration) setMaxSuggestionsPerIteration(reviewConf.maxSuggestionsPerIteration);
    if (reviewConf.reviewerPersonaId) setReviewerPersonaId(reviewConf.reviewerPersonaId);
    if (reviewConf.autoSelectReviewer !== undefined) setAutoSelectReviewer(reviewConf.autoSelectReviewer);
    if (reviewConf.dimensionWeights) setDimensionWeights(prev => ({ ...prev, ...reviewConf.dimensionWeights }));
    if (reviewConf.includeTranscriptExcerpts !== undefined) setIncludeTranscriptExcerpts(reviewConf.includeTranscriptExcerpts);
    if (reviewConf.customPromptSections) {
      setCustomPromptSections(prev => ({
        ...prev,
        systemContext: reviewConf.customPromptSections.systemContext || '',
        evaluationCriteria: reviewConf.customPromptSections.evaluationCriteria || '',
        outputFormat: reviewConf.customPromptSections.outputFormat || ''
      }));
    }
    if (reviewConf.particularFocusText) setParticularFocusText(reviewConf.particularFocusText);

    // Rollback config
    const rollback = initialConfig.autoRollbackConfig || {};
    if (rollback.enabled !== undefined) setAutoRollbackEnabled(rollback.enabled);
    if (rollback.regressionThreshold) setRegressionThreshold(rollback.regressionThreshold);
    if (rollback.compareToIteration) setCompareToIteration(rollback.compareToIteration);

    // Consolidation config
    const consolidation = initialConfig.consolidationConfig || {};
    if (consolidation.enabled !== undefined) setConsolidationEnabled(consolidation.enabled);
    if (consolidation.frequencyIterations) setConsolidationFrequency(consolidation.frequencyIterations);
    if (consolidation.maxPromptLength) setMaxPromptLength(consolidation.maxPromptLength);
  }, [initialConfig, initialName]);

  // Compute filtered personas based on filter selection
  const getFilteredClientPersonas = () => {
    const live = liveClientPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxClientPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const getFilteredTherapistPersonas = () => {
    const live = liveTherapistPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxTherapistPersonas.map(p => ({ ...p, source: 'sandbox' }));

    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live]; // 'all' - sandbox first
  };

  const clientPersonas = getFilteredClientPersonas();
  const therapistPersonas = getFilteredTherapistPersonas();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Build target personas from selected therapist keys with source tracking
      const targetPersonas = targetPersonaKeys.map(key => {
        const persona = therapistPersonas.find(p => p.key === key);
        const source = targetPersonaSources[key] || persona?.source || 'sandbox';
        return {
          personaType: 'therapist',
          personaId: persona?._id,
          personaKey: key,
          source
        };
      });

      // Build client persona config with source tracking
      const clientPersonasConfig = clientPersonaKeys.map(key => ({
        key,
        source: clientPersonaSources[key] || 'live'
      }));

      // Build custom prompt sections (only include non-empty values)
      const customSections = {};
      if (customPromptSections.systemContext?.trim()) {
        customSections.systemContext = customPromptSections.systemContext.trim();
      }
      if (customPromptSections.evaluationCriteria?.trim()) {
        customSections.evaluationCriteria = customPromptSections.evaluationCriteria.trim();
      }
      if (customPromptSections.outputFormat?.trim()) {
        customSections.outputFormat = customPromptSections.outputFormat.trim();
      }

      const config = {
        name,
        description,
        targetPersonas,
        simulationConfig: {
          clientPersonaKeys,
          clientPersonasConfig, // Include source info
          therapistPersonaKeys: targetPersonaKeys,
          maxTurns,
          runsPerIteration,
          // Simulation LLM config - model used to run therapy conversations
          llmConfig: {
            provider: simulationProvider,
            model: simulationModel,
            temperature: simulationTemperature,
            maxTokens: 2048
          },
          maxConcurrentSimulations
        },
        maxIterations,
        qualityThreshold,
        stopConditions: {
          minQualityScore: qualityThreshold,
          maxCostUSD,
          maxDurationMinutes,
          maxConsecutiveNoImprovement
        },
        improvementScope: {
          therapistPrompt: improveTherapistPrompt,
          clientPersonaBehavior: improveClientBehavior,
          therapeuticPathway: false,
          suggestCodeChanges,
          clientPersona: evaluateClientPersona
        },
        reviewConfig: {
          llmConfig: {
            provider: reviewProvider,
            model: reviewModel,
            temperature: reviewTemperature,
            maxTokens: 4096
          },
          // Reviewer persona for modality-specific evaluation
          ...(reviewerPersonaId && { reviewerPersonaId }),
          autoSelectReviewer,
          focusAreas,
          maxSuggestionsPerIteration,
          // Advanced review config (Gap 3)
          dimensionWeights,
          includeTranscriptExcerpts,
          ...(Object.keys(customSections).length > 0 && { customPromptSections: customSections }),
          ...(particularFocusText.trim() && { particularFocusText: particularFocusText.trim() })
        },
        // Auto-Rollback Configuration
        autoRollbackConfig: {
          enabled: autoRollbackEnabled,
          regressionThreshold,
          compareToIteration
        },
        // Consolidation Configuration
        consolidationConfig: {
          enabled: consolidationEnabled,
          frequencyIterations: consolidationFrequency,
          maxPromptLength,
          rewriteApproach: 'full_rewrite'
        }
      };

      const result = await startRLLoop(config, token);
      onLoopStarted?.(result);
    } catch (err) {
      setError(err.error || err.message || 'Failed to start loop');
    } finally {
      setLoading(false);
    }
  };

  const isValid = name && targetPersonaKeys.length > 0 && clientPersonaKeys.length > 0;

  if (loadingPersonas) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Start New RL Loop
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure an automated iterative improvement loop for therapist prompts.
        The system will run simulations, review results, and apply improvements until the quality threshold is met.
      </Typography>

      {/* Persona Filter for Researchers */}
      {isResearcher && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Persona Source Filter
          </Typography>
          <ToggleButtonGroup
            value={personaFilter}
            exclusive
            onChange={(e, value) => value && setPersonaFilter(value)}
            size="small"
          >
            <ToggleButton value="all">
              All Personas
            </ToggleButton>
            <ToggleButton value="sandbox">
              <SandboxIcon sx={{ mr: 0.5 }} fontSize="small" />
              Sandbox Only
            </ToggleButton>
            <ToggleButton value="live">
              <LiveIcon sx={{ mr: 0.5 }} fontSize="small" />
              Live Only
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Sandbox: {sandboxClientPersonas.length} clients, {sandboxTherapistPersonas.length} therapists
          </Typography>
        </Box>
      )}

      {/* Basic Info */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Loop Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., MBT Specialist Improvement Run"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </Grid>
      </Grid>

      {/* Target Personas */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Target Therapist Personas</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the therapist personas whose prompts will be improved.
            <strong> Changes are made to sandbox copies only</strong> - live personas are not modified until you promote them.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Therapist Personas to Improve</InputLabel>
            <Select
              multiple
              value={targetPersonaKeys}
              onChange={(e) => {
                const keys = e.target.value;
                setTargetPersonaKeys(keys);
                // Track sources for newly selected keys
                const newSources = { ...targetPersonaSources };
                keys.forEach(key => {
                  if (!newSources[key]) {
                    const persona = therapistPersonas.find(p => p.key === key);
                    newSources[key] = persona?.source || 'live';
                  }
                });
                setTargetPersonaSources(newSources);
              }}
              input={<OutlinedInput label="Therapist Personas to Improve" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const source = targetPersonaSources[value];
                    return (
                      <Chip
                        key={value}
                        label={source === 'sandbox' ? `${value} (sandbox)` : value}
                        size="small"
                        color={source === 'sandbox' ? 'secondary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {therapistPersonas.map((persona) => (
                <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {persona.source === 'sandbox' && (
                      <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                    )}
                    {persona.name} ({persona.key})
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Simulation Config */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Simulation Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client Personas for Testing</InputLabel>
                <Select
                  multiple
                  value={clientPersonaKeys}
                  onChange={(e) => {
                    const keys = e.target.value;
                    setClientPersonaKeys(keys);
                    // Track sources for newly selected keys
                    const newSources = { ...clientPersonaSources };
                    keys.forEach(key => {
                      if (!newSources[key]) {
                        const persona = clientPersonas.find(p => p.key === key);
                        newSources[key] = persona?.source || 'live';
                      }
                    });
                    setClientPersonaSources(newSources);
                  }}
                  input={<OutlinedInput label="Client Personas for Testing" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const source = clientPersonaSources[value];
                        return (
                          <Chip
                            key={value}
                            label={source === 'sandbox' ? `${value} (sandbox)` : value}
                            size="small"
                            color={source === 'sandbox' ? 'secondary' : 'default'}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {clientPersonas.map((persona) => (
                    <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {persona.source === 'sandbox' && (
                          <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                        )}
                        {persona.name} ({persona.key})
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Max Turns per Simulation: {maxTurns}</Typography>
              <Slider
                value={maxTurns}
                onChange={(e, v) => setMaxTurns(v)}
                min={10}
                max={200}
                marks={[
                  { value: 10, label: '10' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 150, label: '150' },
                  { value: 200, label: '200' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Runs per Iteration: {runsPerIteration}</Typography>
              <Slider
                value={runsPerIteration}
                onChange={(e, v) => setRunsPerIteration(v)}
                min={1}
                max={10}
                marks
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Parallel Simulations: {maxConcurrentSimulations}</Typography>
              <Slider
                value={maxConcurrentSimulations}
                onChange={(e, v) => setMaxConcurrentSimulations(v)}
                min={1}
                max={15}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' }
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Max concurrent simulations per iteration. Higher = faster but uses more API quota.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Stop Conditions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Stop Conditions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Quality Threshold: {qualityThreshold}%</Typography>
              <Slider
                value={qualityThreshold}
                onChange={(e, v) => setQualityThreshold(v)}
                min={50}
                max={100}
                marks={[
                  { value: 50, label: '50%' },
                  { value: 80, label: '80%' },
                  { value: 100, label: '100%' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>Max Iterations: {maxIterations}</Typography>
              <Slider
                value={maxIterations}
                onChange={(e, v) => setMaxIterations(v)}
                min={1}
                max={50}
                marks={[
                  { value: 1, label: '1' },
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Cost ($)"
                value={maxCostUSD}
                onChange={(e) => setMaxCostUSD(parseFloat(e.target.value))}
                inputProps={{ min: 1, max: 500, step: 5 }}
                helperText="USD budget limit"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Duration (min)"
                value={maxDurationMinutes}
                onChange={(e) => setMaxDurationMinutes(parseInt(e.target.value))}
                inputProps={{ min: 5, max: 4320, step: 30 }}
                helperText="Time limit in minutes (max 72 hours)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Plateau Detection"
                value={maxConsecutiveNoImprovement}
                onChange={(e) => setMaxConsecutiveNoImprovement(parseInt(e.target.value))}
                inputProps={{ min: 2, max: 10 }}
                helperText="Stop after N iterations with no improvement"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Auto-Rollback & Consolidation */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Auto-Rollback & Consolidation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure automatic quality protection and prompt maintenance features.
          </Typography>
          <Grid container spacing={2}>
            {/* Auto-Rollback */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRollbackEnabled}
                    onChange={(e) => setAutoRollbackEnabled(e.target.checked)}
                  />
                }
                label="Enable Auto-Rollback on Quality Regression"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Automatically revert prompt changes if quality drops significantly
              </Typography>
            </Grid>

            {autoRollbackEnabled && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Regression Threshold (%)"
                    value={regressionThreshold}
                    onChange={(e) => setRegressionThreshold(parseInt(e.target.value))}
                    inputProps={{ min: 1, max: 20, step: 1 }}
                    helperText="Trigger rollback if quality drops by this percentage"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Compare Quality To</InputLabel>
                    <Select
                      value={compareToIteration}
                      onChange={(e) => setCompareToIteration(e.target.value)}
                      label="Compare Quality To"
                    >
                      <MenuItem value="previous">Previous Iteration</MenuItem>
                      <MenuItem value="best">Best Iteration</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Consolidation */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={consolidationEnabled}
                    onChange={(e) => setConsolidationEnabled(e.target.checked)}
                  />
                }
                label="Enable Periodic Prompt Consolidation"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Periodically rewrite the prompt to remove cruft and contradictions
              </Typography>
            </Grid>

            {consolidationEnabled && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Consolidation Frequency (iterations)"
                    value={consolidationFrequency}
                    onChange={(e) => setConsolidationFrequency(parseInt(e.target.value))}
                    inputProps={{ min: 3, max: 20, step: 1 }}
                    helperText="Consolidate prompt every N iterations"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Prompt Length (chars)"
                    value={maxPromptLength}
                    onChange={(e) => setMaxPromptLength(parseInt(e.target.value))}
                    inputProps={{ min: 2000, max: 15000, step: 500 }}
                    helperText="Trigger consolidation if prompt exceeds this length"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Improvement Scope */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Improvement Scope</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Checkbox
                checked={improveTherapistPrompt}
                onChange={(e) => setImproveTherapistPrompt(e.target.checked)}
              />
            }
            label="Auto-apply therapist prompt improvements"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={improveClientBehavior}
                onChange={(e) => setImproveClientBehavior(e.target.checked)}
              />
            }
            label="Auto-apply client persona behavior fixes"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={suggestCodeChanges}
                onChange={(e) => setSuggestCodeChanges(e.target.checked)}
              />
            }
            label="Suggest code changes (pauses loop for manual review)"
          />
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={evaluateClientPersona}
                onChange={(e) => setEvaluateClientPersona(e.target.checked)}
              />
            }
            label="Evaluate client persona realism"
          />
          {evaluateClientPersona && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: -0.5, mb: 1 }}>
              Runs client-specific realism scoring with 6 authenticity dimensions. Does not auto-apply persona changes.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* LLM Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">LLM Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Simulation LLM Section */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Simulation LLM Model
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Model used to run therapy conversations during simulations.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={simulationProvider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    setSimulationProvider(newProvider);
                    // Get first model from selected provider
                    const models = availableModels[newProvider] || [];
                    if (models.length > 0) {
                      setSimulationModel(models[0].id);
                    }
                  }}
                  label="Provider"
                >
                  {LLM_PROVIDERS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={simulationModel}
                  onChange={(e) => setSimulationModel(e.target.value)}
                  label="Model"
                >
                  {(availableModels[simulationProvider] || []).length > 0 ? (
                    availableModels[simulationProvider].map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.displayName || model.id}
                        {model.isDeprecated && ' (Deprecated)'}
                      </MenuItem>
                    ))
                  ) : (
                    // Fallback to hardcoded defaults if no models loaded
                    (DEFAULT_LLM_MODELS[simulationProvider] || []).map((modelId) => (
                      <MenuItem key={modelId} value={modelId}>{modelId}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Temperature: {simulationTemperature}</Typography>
              <Slider
                value={simulationTemperature}
                onChange={(e, v) => setSimulationTemperature(v)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.7, label: '0.7' },
                  { value: 1, label: '1' }
                ]}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Review LLM Section */}
          <Typography variant="subtitle2" gutterBottom>
            Review LLM Model
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Model used to analyze and grade simulations, generating improvement suggestions.
          </Typography>

          {/* Reviewer Persona Selection */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ReviewIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="subtitle2">Reviewer Persona (Optional)</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a specialized reviewer persona for modality-specific evaluation (e.g., MBT, DBT, CBT).
              The reviewer's system prompt contains criteria tailored to the specific therapy approach.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Reviewer Persona</InputLabel>
                  <Select
                    value={reviewerPersonaId}
                    onChange={(e) => {
                      setReviewerPersonaId(e.target.value);
                      if (e.target.value) {
                        setAutoSelectReviewer(false);
                      }
                    }}
                    label="Reviewer Persona"
                    disabled={autoSelectReviewer}
                  >
                    <MenuItem value="">
                      <em>Generic Reviewer (Default)</em>
                    </MenuItem>
                    {reviewerPersonas.map((persona) => (
                      <MenuItem key={persona._id} value={persona._id}>
                        <Box>
                          <Typography variant="body2">{persona.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {persona.description?.substring(0, 60)}...
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoSelectReviewer}
                      onChange={(e) => {
                        setAutoSelectReviewer(e.target.checked);
                        if (e.target.checked) {
                          setReviewerPersonaId('');
                        }
                      }}
                    />
                  }
                  label="Auto-select"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Match reviewer to target therapist modality
                </Typography>
              </Grid>
            </Grid>
            {reviewerPersonaId && (() => {
              const selectedPersona = reviewerPersonas.find(p => p._id === reviewerPersonaId);
              if (!selectedPersona) return null;
              const rc = selectedPersona.reviewConfiguration;
              return (
                <Box sx={{ mt: 2 }}>
                  {rc?.outputMode && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Output: ${rc.outputMode === 'rich_text' ? 'Rich Text' : 'JSON Only'}`}
                        size="small"
                        color={rc.outputMode === 'rich_text' ? 'info' : 'default'}
                        variant="outlined"
                      />
                      {rc.scoringScale && rc.scoringScale !== 3 && (
                        <Chip
                          label={`Scale: 0-${rc.scoringScale}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}
                  {rc?.evaluationDimensions?.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      <strong>Evaluation dimensions:</strong>{' '}
                      {rc.evaluationDimensions.map(d => d.name.replace(/_/g, ' ')).join(', ')}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 2 }}>LLM Settings {loadingModels && <CircularProgress size={14} sx={{ ml: 1 }} />}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={reviewProvider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    setReviewProvider(newProvider);
                    // Get first model from selected provider
                    const models = availableModels[newProvider] || [];
                    if (models.length > 0) {
                      setReviewModel(models[0].id);
                    }
                  }}
                  label="Provider"
                >
                  {LLM_PROVIDERS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={reviewModel}
                  onChange={(e) => setReviewModel(e.target.value)}
                  label="Model"
                >
                  {(availableModels[reviewProvider] || []).length > 0 ? (
                    availableModels[reviewProvider].map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.displayName || model.id}
                        {model.isDeprecated && ' (Deprecated)'}
                      </MenuItem>
                    ))
                  ) : (
                    // Fallback to hardcoded defaults if no models loaded
                    (DEFAULT_LLM_MODELS[reviewProvider] || []).map((modelId) => (
                      <MenuItem key={modelId} value={modelId}>{modelId}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Temperature: {reviewTemperature}</Typography>
              <Slider
                value={reviewTemperature}
                onChange={(e, v) => setReviewTemperature(v)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.3, label: '0.3' },
                  { value: 1, label: '1' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Focus Areas</InputLabel>
                <Select
                  multiple
                  value={focusAreas}
                  onChange={(e) => setFocusAreas(e.target.value)}
                  input={<OutlinedInput label="Focus Areas" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {FOCUS_AREAS.map((area) => (
                    <MenuItem key={area.value} value={area.value}>
                      {area.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Suggestions"
                value={maxSuggestionsPerIteration}
                onChange={(e) => setMaxSuggestionsPerIteration(parseInt(e.target.value))}
                inputProps={{ min: 1, max: 10 }}
                helperText="Per iteration"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Review Configuration (Gap 3) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <TuneIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">Advanced Review Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fine-tune how the reviewing LLM evaluates simulations. Adjust dimension weights
            to prioritize specific quality aspects, or provide custom prompt sections.
          </Typography>

          {/* Dimension Weights */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Quality Dimension Weights
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Adjust the relative importance of each quality dimension (0-200). Default is 100.
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(dimensionWeights).map(([dimension, weight]) => (
              <Grid item xs={12} md={6} key={dimension}>
                <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {dimension}: {weight}%
                </Typography>
                <Slider
                  value={weight}
                  onChange={(e, v) => setDimensionWeights(prev => ({ ...prev, [dimension]: v }))}
                  min={0}
                  max={200}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Include Transcript Excerpts */}
          <FormControlLabel
            control={
              <Switch
                checked={includeTranscriptExcerpts}
                onChange={(e) => setIncludeTranscriptExcerpts(e.target.checked)}
              />
            }
            label="Include transcript excerpts in review"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
            When enabled, the reviewer will include specific transcript excerpts as evidence for its assessments.
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Custom Prompt Sections */}
          <Typography variant="subtitle2" gutterBottom>
            Custom Prompt Sections (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override default review prompt sections. Leave empty to use defaults.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="System Context Override"
                placeholder="Custom context for the reviewing LLM..."
                value={customPromptSections.systemContext}
                onChange={(e) => setCustomPromptSections(prev => ({ ...prev, systemContext: e.target.value }))}
                helperText="Provide additional context about evaluation goals or domain-specific requirements."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Evaluation Criteria Override"
                placeholder="Custom evaluation criteria..."
                value={customPromptSections.evaluationCriteria}
                onChange={(e) => setCustomPromptSections(prev => ({ ...prev, evaluationCriteria: e.target.value }))}
                helperText="Define specific criteria for evaluating therapist responses."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Output Format Override"
                placeholder="Custom output format instructions..."
                value={customPromptSections.outputFormat}
                onChange={(e) => setCustomPromptSections(prev => ({ ...prev, outputFormat: e.target.value }))}
                helperText="Customize how the reviewer structures its response."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Areas of Particular Focus */}
          <Typography variant="subtitle2" gutterBottom>
            Areas of Particular Focus
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Particular Focus Guidance"
            placeholder="e.g., Pay particular attention to how the therapist responds to client minimization of substance use. The therapist should validate emotions without colluding with minimization..."
            value={particularFocusText}
            onChange={(e) => {
              if (e.target.value.length <= 2000) {
                setParticularFocusText(e.target.value);
              }
            }}
            helperText={`Optional free-text guidance for the reviewer. Describe specific issues or patterns you want the reviewer to emphasize. This supplements (does not replace) the focus areas selected above. ${particularFocusText.length}/2000`}
          />
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 3 }} />

      {/* Submit */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
        >
          {loading ? 'Starting...' : 'Start RL Loop'}
        </Button>
      </Box>
    </Box>
  );
}

export default RLLoopConfigForm;
